"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Check, Smartphone, Calendar, LayoutGrid, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Step = {
  number: string;
  icon: "phone" | "calendar" | "grid" | "pin";
  title: string;
  caption: string;
};
const steps: Step[] = [{
  number: "01",
  icon: "phone",
  title: "Submit Request",
  caption: "Share your project details via our secure portal."
}, {
  number: "02",
  icon: "calendar",
  title: "Schedule Review",
  caption: "Pick a time that aligns with your availability."
}, {
  number: "03",
  icon: "grid",
  title: "Define Scope",
  caption: "Collaborate on the technical requirements."
}, {
  number: "04",
  icon: "pin",
  title: "Final Approval",
  caption: "Confirm the roadmap and start execution."
}];
function StepVisual({
  icon,
  isVisible
}: {
  icon: Step["icon"];
  isVisible: boolean;
}) {
  if (icon === "phone") {
    return <div className="relative flex h-32 w-full items-center justify-center">
        <div className="relative h-28 w-16 rounded-lg border border-primary-200 bg-white transition-all duration-700" style={{
        transform: isVisible ? "scale(1)" : "scale(0.85)",
        opacity: isVisible ? 1 : 0
      }}>
          <div className="absolute left-1/2 top-1 h-1 w-6 -translate-x-1/2 rounded-md bg-primary-200" />
          <div className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg bg-primary-500">
            <Smartphone className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
        </div>
      </div>;
  }
  if (icon === "calendar") {
    const days = ["M", "T", "W", "T", "F"];
    return <div className="relative flex h-32 w-full items-center justify-center">
        <div className="w-full max-w-[200px] rounded-lg border border-primary-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-1.5">
            <SafeHydrate><Calendar className="h-3.5 w-3.5 text-primary-400" strokeWidth={1.5} /></SafeHydrate>
            <span className="text-xs font-medium text-primary-400">{"This week"}</span>
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {days.map((d, i) => <div data-index={i} key={i} className="flex flex-col items-center gap-1">
                <span className="text-[10px] font-medium text-primary-400">{d}</span>
                <div className={`h-7 w-full rounded-md transition-all duration-500 ${i === 1 ? "bg-primary-500" : "bg-primary-100"}`} style={{
              transitionDelay: `${i * 80}ms`,
              transform: isVisible && i === 1 ? "scale(1.05)" : "scale(1)"
            }} />
              </div>)}
          </div>
        </div>
      </div>;
  }
  if (icon === "grid") {
    return <div className="relative flex h-32 w-full items-center justify-center">
        <div className="rounded-lg border border-primary-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-1.5">
            <LayoutGrid className="h-3.5 w-3.5 text-primary-400" strokeWidth={1.5} />
            <span className="text-xs font-medium text-primary-400">{"Project Map"}</span>
          </div>
          <div className="grid grid-cols-6 gap-1">
            {Array.from({
            length: 24
          }).map((_, i) => {
            const isHighlight = i === 14;
            const isOccupied = [2, 5, 9, 11, 18, 21].includes(i);
            return <div data-index={i} key={i} className={`h-3.5 w-3.5 rounded transition-all duration-500 ${isHighlight ? "bg-primary-500" : isOccupied ? "bg-primary-200" : "bg-primary-100"}`} style={{
              transform: isVisible && isHighlight ? "scale(1.15)" : "scale(1)",
              boxShadow: isVisible && isHighlight ? "0 0 0 3px hsl(220 70% 40% / 0.18)" : "none"
            }} />;
          })}
          </div>
        </div>
      </div>;
  }
  return <div className="relative flex h-32 w-full items-center justify-center">
      <div className="flex items-center gap-2 rounded-lg border border-primary-200 bg-white px-4 py-2.5 transition-all duration-700" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(8px)"
    }}>
        <MapPin className="h-4 w-4 text-primary-500" strokeWidth={1.5} />
        <span className="font-sans text-sm font-medium text-primary-900">
          {"Phase 1 · Approved"}
        </span>
        <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary-500 transition-all duration-500" style={{
        transitionDelay: "400ms",
        transform: isVisible ? "scale(1)" : "scale(0)"
      }}>
          <Check className="h-3 w-3 text-white" strokeWidth={2.5} />
        </div>
      </div>
    </div>;
}
function StepCard({
  step,
  index
}: {
  step: Step;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [hovered, setHovered] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  useEffect(() => {
    if (hovered) setAnimationKey(k => k + 1);
  }, [hovered]);
  return <div data-index={index} ref={ref} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(16px)",
    transition: `opacity 560ms cubic-bezier(0.2, 0.7, 0.1, 1) ${index * 60}ms, transform 560ms cubic-bezier(0.2, 0.7, 0.1, 1) ${index * 60}ms`
  }}>
      <Card className="group relative h-full min-h-[360px] gap-0 border border-primary-200 bg-white p-7 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md">
        <span className="font-sans text-sm font-semibold tracking-wide text-primary-400">
          {step.number}
        </span>
        <div className="mt-6 flex-1" key={animationKey}>
          <StepVisual icon={step.icon} isVisible={isVisible || hovered} />
        </div>
        <div className="mt-6">
          <h3 className="font-default text-2xl font-medium leading-tight tracking-tight text-primary-900">
            {step.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-primary-700">
            {step.caption}
          </p>
        </div>
      </Card>
    </div>;
}
export default function Requirements() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="16568" id="requirements-process" className="bg-primary-100 py-24 md:py-32">
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8 lg:px-14">
        <div ref={headRef} className="max-w-[640px]" style={{
        opacity: headVisible ? 1 : 0,
        transform: headVisible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 600ms cubic-bezier(0.2, 0.7, 0.1, 1), transform 600ms cubic-bezier(0.2, 0.7, 0.1, 1)"
      }}>
          <span className="inline-block text-[13px] font-semibold uppercase tracking-[0.04em] text-primary-500">
            {"Our Workflow"}
          </span>
          <h2 className="mt-5 font-default text-4xl font-medium leading-[1.05] tracking-tight text-primary-900 sm:text-5xl md:text-[3.5rem] lg:text-6xl">
            {"From initial concept to "}
            <em className="italic text-primary-500">{"final delivery"}</em>
            {" in four steps."}
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => <StepCard key={step.number} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}